import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import {
  DEFAULT_PAGINATION_WINDOW,
  getLeftRange,
  getMiddleRange,
  getRightRange,
  LEFT_GAP,
  RIGHT_GAP,
} from './pagination_helpers';


interface Props {
  limit: number;
  offset: number;
  total: number;
  previousButton?: React.ReactElement;
  onPreviousClick?: () => any;
  nextButton?: React.ReactElement;
  onNextClick?: () => any;
  renderPageNumberLink?: (pageNum: number) => any;
  onPageNumberClick?: (pageNum: number) => any;
  paginationWindow?: number;
}

function getCurrentPage(limit, offset) {
  const page = Math.ceil(offset / limit) + 1;
  return page < 1 ? 1 : page;
}

function EllipsisLink({ pageNum }) {
  const key = `pagination-gap-${pageNum}`;
  const classes = classNames(
    'pagination__button',
    'pagination__button--gap',
  );

  return (
    <div
      key={key}
      className={classes}
      aria-hidden="true"
      data-disabled
    >
      &hellip;
    </div>
  );
}

function PaginationButton({ text, onClick, classes = 'pagination__button' }) {
  return (
    <button
      className={classes}
      onClick={onClick}
    >
      {text}
    </button>
  );
}

export default function PaginationControls({ total, offset, limit, ...props }: Props) {
  const totalPages = Math.ceil(total / limit);
  const paginationWindow = props.paginationWindow || DEFAULT_PAGINATION_WINDOW;

  if (totalPages <= 1 || (offset >= total)) { return null; }

  const currentPage = getCurrentPage(limit, offset);
  const middleRange = getMiddleRange(currentPage, paginationWindow, totalPages);
  const leftRange = getLeftRange(paginationWindow, middleRange);
  const rightRange = getRightRange(totalPages, middleRange);
  const pageNumbers = leftRange.concat(middleRange).concat(rightRange);

  const previousButton = props.previousButton || (
    props.onPreviousClick &&
    <PaginationButton
      text={I18n.t('commons.paginationControls.previous')}
      onClick={props.onPreviousClick}
    />
  );
  const nextButton = props.nextButton || (
    props.onNextClick &&
    <PaginationButton
      text={I18n.t('commons.paginationControls.next')}
      onClick={props.onNextClick}
    />
  );

  return (
    <div className="pagination-container">
      <div className="pagination__buttons">
        {currentPage !== 1 && previousButton}

        {pageNumbers.map((pageNum) => {
          if (pageNum === LEFT_GAP || pageNum === RIGHT_GAP) {
            return (
              <EllipsisLink key={pageNum} pageNum={pageNum} />
            );
          } else if (props.renderPageNumberLink) {
            return (
              <React.Fragment key={pageNum}>
                {props.renderPageNumberLink(pageNum)}
              </React.Fragment>
            );
          } else if (props.onPageNumberClick) {
            return (
              <PaginationButton
                key={pageNum}
                text={pageNum.toString()}
                onClick={() => { props.onPageNumberClick(pageNum); }}
                classes={classNames(
                  'pagination__button',
                  'pagination__button--number',
                  { 'pagination__button--active': pageNum === currentPage },
                )}
              />
            );
          }
        })}

        {currentPage !== totalPages && nextButton}
      </div>
    </div>
  );
}
